// iPhone Safari does two things when a form field is focused:
//   1. If the field's text is under 16px, or it sits inside a transformed
//      modal, Safari zooms the whole page.
//   2. The keyboard covers the bottom of a fixed modal, so Save disappears.
// This keeps the designed layout and shrinks the open modal to the area
// that is actually visible above the keyboard.

const isField = (node) =>
  node instanceof HTMLInputElement ||
  node instanceof HTMLTextAreaElement ||
  node instanceof HTMLSelectElement;

const isMobile = () => window.matchMedia("(max-width: 768px)").matches;

export const installMobileFormLayout = () => {
  const viewport = window.visualViewport;
  if (!viewport) return () => {};

  let activeModal = null;

  const reset = (modal) => {
    if (!modal) return;
    const container = modal.closest(".chakra-modal__content-container");
    modal.style.height = "";
    modal.style.maxHeight = "";
    if (!container) return;
    container.style.top = "";
    container.style.height = "";
    container.style.bottom = "";
  };

  const fit = () => {
    if (!activeModal || !document.body.contains(activeModal)) {
      activeModal = null;
      return;
    }
    const container = activeModal.closest(".chakra-modal__content-container");
    const height = `${Math.round(viewport.height)}px`;
    const top = `${Math.round(viewport.offsetTop)}px`;
    if (container) {
      container.style.top = top;
      container.style.height = height;
      container.style.bottom = "auto";
    }
    activeModal.style.height = height;
    activeModal.style.maxHeight = height;
  };

  const onViewportChange = () => {
    if (activeModal) fit();
  };

  const onFocusIn = (event) => {
    if (!isMobile()) return;
    const field = event.target;
    if (!isField(field)) return;
    const modal = field.closest(".chakra-modal__content");
    if (!modal || modal.getAttribute("role") === "alertdialog") return;
    if (activeModal && activeModal !== modal) reset(activeModal);
    activeModal = modal;
    fit();
    window.setTimeout(() => {
      if (!activeModal || !activeModal.contains(field)) return;
      field.scrollIntoView({ block: "center", inline: "nearest" });
    }, 60);
  };

  const onFocusOut = () => {
    window.setTimeout(() => {
      const next = document.activeElement;
      if (activeModal && next && activeModal.contains(next) && isField(next)) return;
      reset(activeModal);
      activeModal = null;
    }, 80);
  };

  viewport.addEventListener("resize", onViewportChange);
  viewport.addEventListener("scroll", onViewportChange);
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);

  return () => {
    viewport.removeEventListener("resize", onViewportChange);
    viewport.removeEventListener("scroll", onViewportChange);
    document.removeEventListener("focusin", onFocusIn);
    document.removeEventListener("focusout", onFocusOut);
    reset(activeModal);
  };
};
